import { createContext, useCallback, useContext, useState, useEffect, type ReactNode } from 'react';
import { subscriptionsApi } from '@/services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar?: string;
  phone?: string;
  store?: {
    id: number;
    name: string;
    slug?: string;
    currency?: string;
  };
}

interface Subscription {
  id: number;
  status: string;
  plan_id: number;
  starts_at?: string | null;
  renews_at?: string | null;
  plan?: {
    id: number;
    name: string;
    slug: string;
    is_free: boolean;
    features?: string[];
    limits?: Record<string, number>;
  };
}

interface AuthContextType {
  // Admin/Merchant Auth
  isAdminAuthenticated: boolean;
  adminUser: User | null;
  adminToken: string | null;
  adminLogin: (user: User, token: string) => void;
  adminLogout: () => void;
  updateAdminUser: (user: Partial<User>) => void;
  currentSubscription: Subscription | null;
  isSubscriptionLoading: boolean;
  refreshSubscription: () => Promise<Subscription | null>;
  
  // Customer Auth
  isCustomerAuthenticated: boolean;
  customerUser: User | null;
  customerToken: string | null;
  customerLogin: (user: User, token: string) => void;
  customerLogout: () => void;
  updateCustomerUser: (user: Partial<User>) => void;
  
  // Shared
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Admin/Merchant State
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState<User | null>(null);
  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  
  // Customer State
  const [isCustomerAuthenticated, setIsCustomerAuthenticated] = useState(false);
  const [customerUser, setCustomerUser] = useState<User | null>(null);
  const [customerToken, setCustomerToken] = useState<string | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);

  const loadSubscription = useCallback(async (user: User | null) => {
    if (!user || user.role !== 'merchant') {
      setCurrentSubscription(null);
      setIsSubscriptionLoading(false);
      return null;
    }

    setIsSubscriptionLoading(true);
    try {
      const response = await subscriptionsApi.getCurrent(user.store?.id);
      const subscription = response.data || null;
      setCurrentSubscription(subscription);
      return subscription;
    } catch (error) {
      console.error('Failed to load subscription', error);
      setCurrentSubscription(null);
      return null;
    } finally {
      setIsSubscriptionLoading(false);
    }
  }, []);

  const refreshSubscription = useCallback(
    () => loadSubscription(adminUser),
    [adminUser, loadSubscription],
  );

  useEffect(() => {
    const initialize = async () => {
    // Check Admin/Merchant Auth
    const savedAdminToken = localStorage.getItem('rork_admin_token');
    const savedAdminUser = localStorage.getItem('rork_admin_user');
    if (savedAdminToken && savedAdminUser) {
      try {
        const parsedUser = JSON.parse(savedAdminUser) as User;
        setAdminToken(savedAdminToken);
        setAdminUser(parsedUser);
        setIsAdminAuthenticated(true);
        await loadSubscription(parsedUser);
      } catch {
        localStorage.removeItem('rork_admin_token');
        localStorage.removeItem('rork_admin_user');
      }
    }

    // Check Customer Auth
    const savedCustomerToken = localStorage.getItem('rork_customer_token');
    const savedCustomerUser = localStorage.getItem('rork_customer_user');
    if (savedCustomerToken && savedCustomerUser) {
      setCustomerToken(savedCustomerToken);
      setCustomerUser(JSON.parse(savedCustomerUser));
      setIsCustomerAuthenticated(true);
    }
    
      setIsLoading(false);
    };

    void initialize();
  }, [loadSubscription]);

  const adminLogin = (userData: User, userToken: string) => {
    localStorage.setItem('rork_admin_token', userToken);
    localStorage.setItem('rork_admin_user', JSON.stringify(userData));
    setAdminToken(userToken);
    setAdminUser(userData);
    setIsAdminAuthenticated(true);
    void loadSubscription(userData);
  };

  const adminLogout = () => {
    localStorage.removeItem('rork_admin_token');
    localStorage.removeItem('rork_admin_user');
    setAdminToken(null);
    setAdminUser(null);
    setIsAdminAuthenticated(false);
    setCurrentSubscription(null);
    setIsSubscriptionLoading(false);
  };

  const customerLogin = (userData: User, userToken: string) => {
    localStorage.setItem('rork_customer_token', userToken);
    localStorage.setItem('rork_customer_user', JSON.stringify(userData));
    setCustomerToken(userToken);
    setCustomerUser(userData);
    setIsCustomerAuthenticated(true);
  };

  const customerLogout = () => {
    localStorage.removeItem('rork_customer_token');
    localStorage.removeItem('rork_customer_user');
    setCustomerToken(null);
    setCustomerUser(null);
    setIsCustomerAuthenticated(false);
  };

  // Update user without logging out
  const updateAdminUser = (updates: Partial<User>) => {
    setAdminUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('rork_admin_user', JSON.stringify(updated));
      return updated;
    });
  };

  const updateCustomerUser = (updates: Partial<User>) => {
    setCustomerUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      localStorage.setItem('rork_customer_user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ 
      // Admin/Merchant
      isAdminAuthenticated,
      adminUser,
      adminToken,
      adminLogin,
      adminLogout,
      updateAdminUser,
      currentSubscription,
      isSubscriptionLoading,
      refreshSubscription,
      
      // Customer
      isCustomerAuthenticated,
      customerUser,
      customerToken,
      customerLogin,
      customerLogout,
      updateCustomerUser,
      
      isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
